import type { FC } from 'react';
import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { Loading, PublicLayout, useAuthContext } from '@catalyze/ui';
import { useCreatePasswordMutation } from 'api/password/createPassword';
import { useValidateEmailTokenMutation } from 'api/session/validateEmailToken';
import { Routes } from 'constants/routes';
import type { ICreatePasswordForm } from 'features/CreatePassword/components/ChangePasswordForm';
import { ChangePasswordForm } from 'features/CreatePassword/components/ChangePasswordForm';
import {
    EmailTokenCheckInbox,
    EmailTokenErrorMsg,
    ErrorInfoMsg,
} from 'features/CreatePassword/components/StatusMessages';
import { Navigation } from 'features/SignUp/components/Navigation';

type TCreatePasswordRouteParams = {
    emailToken: string;
};

export const CreatePassword: FC = () => {
    const navigate = useNavigate();
    const value = useAuthContext();
    const { storeSignedUser } = value;

    const { isIdle, isError, mutate } = useCreatePasswordMutation({
        onSuccess: (data) => {
            // If we are creating password, we sign in user and redirect to subscription page
            storeSignedUser({
                accessToken: data.accessToken,
                refreshToken: data.refreshToken,
                user: data.user,
            });
            navigate(Routes.SUBSCRIPTION);
        },
    });

    const {
        isError: isEmailValidationError,
        mutate: validateEmailToken,
        isLoading,
    } = useValidateEmailTokenMutation({});

    const { emailToken } = useParams<TCreatePasswordRouteParams>();

    useEffect(() => {
        if (emailToken) {
            validateEmailToken({ token: emailToken });
        }
    }, [emailToken, validateEmailToken]);

    const onFormSubmit = (data: ICreatePasswordForm) => {
        mutate({
            emailToken: emailToken ?? '',
            password: data.password,
        });
    };

    return (
        <PublicLayout navigation={Navigation}>
            {isLoading && <Loading />}
            {!emailToken && <EmailTokenCheckInbox />}
            {emailToken && isEmailValidationError && <EmailTokenErrorMsg />}
            {!isLoading && emailToken && !isEmailValidationError && (
                <>
                    {isError && <ErrorInfoMsg />}
                    {isIdle && <ChangePasswordForm onSubmit={onFormSubmit} />}
                </>
            )}
        </PublicLayout>
    );
};
